import pino from 'pino';

import { composites } from '../../../../common/constants/access';
import { companyPropertiesConstants, overviewConstants } from '../../../constants/appConstants';
import { companyFeaturesQuery, companyPropertiesQuery } from '../../../queries/companyQuery';
import { getProfilesQuery, requestProfilesQuery } from '../../../queries/requestProfilesQuery';
import {
	getProviderMappingConfigsQuery,
	getProviderMappingQuery,
	getServerVerionQuery,
	getWorkflowConfigQuery,
	getWorkflowConfigsQuery,
} from '../../../queries/supportQuery';
import { getVerifyUserAccessQuery, userQuery } from '../../../queries/userQuery';
import { isCompanyOrSupportAdmin } from '../../../selectors/userSelectors';

import type { QueryClient } from '@tanstack/react-query';

type RouteParams = {
	teamId: unknown;
};

export const supportSettingsLoader = async ({
	queryClient,
	params,
}: {
	queryClient: QueryClient;
	params: RouteParams;
}) => {
	const logger = pino();
	const verifyUserAccessQuery = getVerifyUserAccessQuery((composites as any).WRITE_COMPANY);
	const profilesQuery = getProfilesQuery();
	const serverVersionQuery = getServerVerionQuery();
	const providerMappingQuery = getProviderMappingQuery();
	const workflowConfigsQuery = getWorkflowConfigsQuery();
	const providerMappingConfigsQuery = getProviderMappingConfigsQuery();

	const userData = await queryClient.ensureQueryData(userQuery);
	const userRole = isCompanyOrSupportAdmin(userData.roles)
		? overviewConstants.COMPANY_ADMIN
		: userData.roles.find(role => role.securityGroupId === params.teamId)?.roleId || '';

	const companyProperties = await queryClient.ensureQueryData(
		companyPropertiesQuery(userData.companyAccountId, { showactivity: companyPropertiesConstants.showactivity }),
	);

	const workflowConfigQuery = getWorkflowConfigQuery({ companyId: userData.companyAccountId });

	const serverVersion = await queryClient.ensureQueryData({
		queryKey: serverVersionQuery.queryKey,
		queryFn: serverVersionQuery.queryFn,
	});

	const config = await queryClient.ensureQueryData({
		queryKey: workflowConfigQuery.queryKey,
		queryFn: workflowConfigQuery.queryFn,
	});

	const workflowConfigs = await queryClient.ensureQueryData({
		queryKey: workflowConfigsQuery.queryKey,
		queryFn: workflowConfigsQuery.queryFn,
	});

	const hasAccess = await queryClient.ensureQueryData({
		queryKey: verifyUserAccessQuery.queryKey,
		queryFn: verifyUserAccessQuery.queryFn,
	});

	let companyFeatures;
	try {
		companyFeatures = await queryClient.ensureQueryData(companyFeaturesQuery);
	} catch (error) {
		logger.error(error);
	}

	const profiles = await queryClient.ensureQueryData({
		queryKey: profilesQuery.queryKey,
		queryFn: profilesQuery.queryFn,
	});

	const requestProfiles = await queryClient.ensureQueryData(requestProfilesQuery(userData.companyAccountId, false));

	const providerMappingConfigs = await queryClient.ensureQueryData({
		queryKey: providerMappingConfigsQuery.queryKey,
		queryFn: providerMappingConfigsQuery.queryFn,
	});

	const providerMap = await queryClient.ensureQueryData(providerMappingQuery);

	return {
		userRole,
		userData,
		hasAccess,
		profiles,
		providerMap,
		companyFeatures,
		serverVersion,
		workflowConfigs,
		requestProfiles,
		configId: config.id as string,
		providerMappingConfigs,
		companyProperties,
	};
};
